import { useQuery } from "@tanstack/react-query";
import { getUserInfo } from "../../api/user";
import { PersonStamdata } from "../../interfaces/stamdata";
import { useDelegationInvitations } from "../../hooks/delegation/useDelegationInvitations";
import { useActiveDelegations } from "../../hooks/delegation/useActiveDelegations";
import { useDelegationHistory } from "../../hooks/delegation/useDelegationHistory";
import { AccordionBody, AccordionHeader, AccordionItem } from "../Accordion";
import Spinner from "../graphics/Spinner";
import DelegationActiveTable from "./delegation/DelegationActiveTable";
import DelegationHistoryTable from "./delegation/DelegationHistoryTable";
import DelegationInvitationsTable from "./delegation/DelegationInvitationsTable";
import DelegationInviteModal from "./delegation/DelegationInviteModal";

const AccountDelegation = ({
  stamdata,
}: {
  stamdata: PersonStamdata | null;
}) => {
  const { data: userInfo } = useQuery({
    queryKey: ["userInfo"],
    queryFn: getUserInfo,
  });

  const isDelegatedSession = userInfo?.isDelegatedSession ?? false;
  const isTokenUser = userInfo?.role === "TokenUser";
  const canManage = Boolean(stamdata) && !isTokenUser && !isDelegatedSession;

  const invitationsQuery = useDelegationInvitations(canManage);
  const activeQuery = useActiveDelegations(canManage);
  const historyQuery = useDelegationHistory(canManage);

  if (!stamdata) {
    return null;
  }

  if (isDelegatedSession) {
    return (
      <AccordionItem value="fuldmagt">
        <AccordionHeader>Fuldmagt login</AccordionHeader>
        <AccordionBody>
          <div className="alert alert-info mb-0" role="status">
            Du er logget ind som fuldmægtig for {userInfo?.navn}. Fuldmagter kan
            kun administreres af advokaten selv.
          </div>
        </AccordionBody>
      </AccordionItem>
    );
  }

  if (isTokenUser) {
    return null;
  }

  return (
    <AccordionItem value="fuldmagt">
      <AccordionHeader>Fuldmagt login</AccordionHeader>
      <AccordionBody>
        <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3 mb-4">
          <p className="text-muted mb-0">
            Invitér en person til at logge ind og handle på dine vegne
          </p>
          <DelegationInviteModal />
        </div>

        <section className="mb-5">
          <h2 className="fs-5 fw-semibold mb-3">Afventende invitationer</h2>
          {invitationsQuery.isLoading ? (
            <Spinner size={24} label="Henter invitationer..." />
          ) : invitationsQuery.isError ? (
            <div className="alert alert-danger mb-0" role="alert">
              Kunne ikke hente invitationer.
            </div>
          ) : (
            <DelegationInvitationsTable
              invitations={invitationsQuery.data ?? []}
            />
          )}
        </section>

        <section>
          <h2 className="fs-5 fw-semibold mb-3">Aktive fuldmagter</h2>
          {activeQuery.isLoading ? (
            <Spinner size={24} label="Henter fuldmagter..." />
          ) : activeQuery.isError ? (
            <div className="alert alert-danger mb-0" role="alert">
              Kunne ikke hente fuldmagter.
            </div>
          ) : (
            <DelegationActiveTable delegations={activeQuery.data ?? []} />
          )}
        </section>

        {historyQuery.data && historyQuery.data.length > 0 ? (
          <section className="mt-5">
            <h2 className="fs-5 fw-semibold mb-3">Tidligere fuldmagter</h2>
            <DelegationHistoryTable delegations={historyQuery.data} />
          </section>
        ) : null}
      </AccordionBody>
    </AccordionItem>
  );
};

export default AccountDelegation;
